﻿import { useMsal } from '@azure/msal-react';
import { Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useStores, useTitle } from '../../common/hooks';
import { blankQuestionnaire, blankUploadedMedia, blankWinnerAddress } from '../../models';
import { QuestionnaireRouteParams } from '../../models/route-params/questionnaire-route-params';
import { RoutePaths } from '../../route-paths';
import { StepNav, StepRail, StepRailStatus, StepperSkeleton } from '../../shared/stepper';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import NoAccess from '../shared-components/NoAccess';
import { ContactInformationSection } from './components/ContactInformationSection';
import { EventAttendeesSection } from './components/EventAttendeesSection';
import { QuestionnaireWizardSection } from './components/QuestionnaireWizardSection';
import { UploadLogoSection } from './components/UploadLogoSection';
import { UploadMediaSection } from './components/UploadMediaSection';
import {
  QuestionnaireStepId,
  QuestionnaireStepperInstance,
  questionnaireStepper,
} from './questionnaire-stepper';
import './QuestionnaireWizardPage.scss';

const CONTACT_DESCRIPTION = 'In order to officially accept your award, please confirm the following details by September 23, 2026. This information will be used for the promotion and celebration of your award.';

type QuestionnaireWizardLoadingType = 'load' | 'save';

const QuestionnaireWizardPageImpl: React.FC = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const {
    winnerId,
    questionnaireId,
  } = useParams<QuestionnaireRouteParams>();

  const {
    apiStore,
    awardStore,
    questionnaireWizardPageUiStore,
  } = useStores();

  const [loadingType, setLoadingType] = useState<QuestionnaireWizardLoadingType | undefined>('load');
  const [isForbiddenAccess, setIsForbiddenAccess] = useState(false);

  const preloadData = async (): Promise<void> => {
    if ((!winnerId && !questionnaireId) || !awardStore.defaultProgramRef) {
      setLoadingType(undefined);
      return;
    }

    try {
      await loadQuestionnaireProgress(awardStore.defaultProgramRef.programId);
    } catch (error) {
      if ((error as { statusCode: number })?.statusCode === 403) {
        setIsForbiddenAccess(true);
      }
    } finally {
      setLoadingType(undefined);
    }
  };

  const loadQuestionnaireProgress = async (programId: number): Promise<void> => {
    const parsedQuestionnaireId = questionnaireId ? Number(questionnaireId) : 0;
    if (parsedQuestionnaireId) {
      const questionnaire = await apiStore.getQuestionnaire(instance, parsedQuestionnaireId);
      if (!questionnaire.contact.addresses.some(address => address.addressTypeCode === 'Celebratory-Item')) {
        questionnaire.contact.addresses.push(blankWinnerAddress(parsedQuestionnaireId, 'Celebratory-Item'));
      }

      questionnaireWizardPageUiStore.setQuestionnaire(questionnaire);
    } else {
      questionnaireWizardPageUiStore.setQuestionnaire(blankQuestionnaire());
    }

    const parsedWinnerId = parsedQuestionnaireId ? questionnaireWizardPageUiStore.questionnaire.winnerId : Number(winnerId);
    questionnaireWizardPageUiStore.setWinnerId(parsedWinnerId);

    const awardedNominations = await apiStore.getAwardedNominations(instance);
    questionnaireWizardPageUiStore.setAwardedNominations(awardedNominations);

    const winnerOrg = await apiStore.getWinnerOrganization(instance, parsedWinnerId);
    questionnaireWizardPageUiStore.setContact(winnerOrg);
    //TODO - use get winner events by winner Id API
    const winnerEvents = await apiStore.getWinnerEvents(instance, programId);
    questionnaireWizardPageUiStore.setWinnerEvents(winnerEvents);

    if (!parsedQuestionnaireId) return;

    const questionnaireProgress = await apiStore.getQuestionnaireProgress(instance, parsedQuestionnaireId);
    if (questionnaireProgress) {
      const furthestStepId = questionnaireStepper.parseStep(questionnaireProgress.furthestQuestionnaireStepCode) ?? 'contact';
      questionnaireWizardPageUiStore.setQuestionnaireProgress(questionnaireProgress);
      questionnaireWizardPageUiStore.setFurthestVisitedStepId(furthestStepId);
      await validateCompletedSteps(questionnaireWizardPageUiStore.visibleStepIds, furthestStepId);
      questionnaireWizardPageUiStore.setInitialStepId(questionnaireStepper.parseStep(questionnaireProgress.latestVisitedQuestionnaireStepCode) ?? 'contact');
    }
  };

  const validateStep = (stepId: QuestionnaireStepId): boolean => {
    return questionnaireWizardPageUiStore.isStepValid(stepId);
  };

  const markCompletedStepsValidated = (stepIds: QuestionnaireStepId[], furthestStepId: QuestionnaireStepId): QuestionnaireStepId[] => {
    const furthestStepIndex = stepIds.findIndex(stepId => stepId === furthestStepId);
    const completedStepIds = stepIds.slice(0, furthestStepIndex + 1);
    completedStepIds.forEach((stepId) => questionnaireWizardPageUiStore.markStepValidated(stepId));
    return completedStepIds;
  };

  const validateCompletedSteps = async (stepIds: QuestionnaireStepId[], furthestStepId: QuestionnaireStepId): Promise<boolean> => {
    const completedStepIds = markCompletedStepsValidated(stepIds, furthestStepId);
    const validationInput = questionnaireWizardPageUiStore.getValidationInput();
    await Promise.all(
      completedStepIds.map((stepId) => questionnaireStepper.validate(stepId, validationInput)),
    );

    return completedStepIds.every(validateStep);
  };

  const createOrUpdateContactAndAddress = async (): Promise<void> => {
    try {
      const celebratoryItemAddress = questionnaireWizardPageUiStore.questionnaire.contact.addresses?.find(
        (address) => address.addressTypeCode === 'Celebratory-Item'
      );

      if (!questionnaireWizardPageUiStore.questionnaire.questionnaireId) {
        const questionnaireRef = await apiStore.createQuestionnaire(
          instance,
          questionnaireWizardPageUiStore.questionnaire.winnerId,
          questionnaireWizardPageUiStore.questionnaire
        );
        questionnaireWizardPageUiStore.setQuestionnaireId(questionnaireRef.questionnaireId);
      }

      const operations = [
        apiStore.updateWinnerContact(instance, questionnaireWizardPageUiStore.questionnaire.questionnaireId, questionnaireWizardPageUiStore.questionnaire.contact),
      ];

      if (celebratoryItemAddress && questionnaireWizardPageUiStore.winnerRole !== 'Finalist') {
        operations.push(
          apiStore.updateWinnerAddress(
            instance,
            questionnaireWizardPageUiStore.questionnaire.questionnaireId,
            'Celebratory-Item',
            celebratoryItemAddress
          )
        );
      }

      await Promise.all(operations);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const createOrUpdateEventAttendees = async (): Promise<void> => {
    try {
      if (questionnaireWizardPageUiStore.deletedEventAttendeeIds.length > 0) {
        await Promise.allSettled(
          questionnaireWizardPageUiStore.deletedEventAttendeeIds.map(async (eventAttendeeId) => {
            await apiStore.deleteEventAttendee(instance, eventAttendeeId);
          })
        );

        questionnaireWizardPageUiStore.deletedEventAttendeeIds.length = 0;
      }

      const operations = questionnaireWizardPageUiStore.questionnaire.eventAttendees.map(async (eventAttendee) => {
        if (!eventAttendee.eventAttendeeId) {
          const eventAttendeeRef = await apiStore.createEventAttendee(
            instance,
            questionnaireWizardPageUiStore.questionnaire.questionnaireId,
            eventAttendee
          );
          eventAttendee.eventAttendeeId = eventAttendeeRef.eventAttendeeId;
        } else {
          await apiStore.updateEventAttendee(
            instance,
            eventAttendee.eventAttendeeId,
            eventAttendee
          );
        }
      });

      await Promise.allSettled(operations);
    } catch (error) {
      // TODO - handle error
    }
  };

  const updateQuestionnaire = async (): Promise<void> => {
    try {
      await apiStore.updateQuestionnaire(instance, questionnaireWizardPageUiStore.questionnaire.questionnaireId, {
        mediaConsentDisclaimer: questionnaireWizardPageUiStore.questionnaire.mediaConsentDisclaimer
      });
    } catch (error) {
      // TODO - handle error
    }
  };

  const getSaveTask = (stepId: QuestionnaireStepId): (() => Promise<void>) | undefined => {
    switch (stepId) {
      case 'contact': return createOrUpdateContactAndAddress;
      case 'event-attendees': return createOrUpdateEventAttendees;
      case 'media': return updateQuestionnaire;
      default: return undefined;
    }
  };

  const isWizardFormValid = async (stepId: QuestionnaireStepId): Promise<boolean> => {
    return validateCompletedSteps(questionnaireWizardPageUiStore.visibleStepIds, stepId);
  };

  const updateProgress = async (toStepId: QuestionnaireStepId): Promise<void> => {
    const furthestStepId = questionnaireWizardPageUiStore.furthestVisitedStepId;
    const furthestIndex = questionnaireWizardPageUiStore.visibleStepIds.findIndex(stepId => stepId === furthestStepId);
    const toIndex = questionnaireWizardPageUiStore.visibleStepIds.findIndex(stepId => stepId === toStepId);
    const newFurthestStepId = toIndex > furthestIndex ? toStepId : furthestStepId;

    questionnaireWizardPageUiStore.setFurthestVisitedStepId(newFurthestStepId);
    await apiStore.updateQuestionnaireProgress(instance, questionnaireWizardPageUiStore.questionnaireId, {
      furthestQuestionnaireStepCode: newFurthestStepId,
      latestVisitedQuestionnaireStepCode: toStepId,
    });
  };

  const commitStep = async (fromStepId: QuestionnaireStepId, toStepId: QuestionnaireStepId): Promise<void> => {
    const saveTask = getSaveTask(fromStepId);

    setLoadingType('save');
    try {
      await saveTask?.();
      await updateProgress(toStepId);
    } finally {
      setLoadingType(undefined);
    }
  };

  const handleBeforeStepChange = async (fromStepId: QuestionnaireStepId, toStepId: QuestionnaireStepId): Promise<boolean> => {
    const isForward = questionnaireWizardPageUiStore.visibleStepIds.indexOf(toStepId) > questionnaireWizardPageUiStore.visibleStepIds.indexOf(fromStepId);
    const isCompletedStepsValid = await validateCompletedSteps(questionnaireWizardPageUiStore.visibleStepIds, fromStepId);
    if (isForward && !isCompletedStepsValid) {
      return false;
    }

    await commitStep(fromStepId, toStepId);
    return true;
  };

  const uploadLogo = async (mediaContent: File): Promise<void> => {
    setLoadingType('save');
    try {
      const uploadedMediaRef = await apiStore.uploadLogo(instance, questionnaireWizardPageUiStore.questionnaire.questionnaireId, mediaContent);
      questionnaireWizardPageUiStore.questionnaire.logo.uploadedMediaId = uploadedMediaRef.uploadedMediaId;
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoadingType(undefined);
    }
  };

  const deleteLogo = async (uploadedMediaId: number): Promise<void> => {
    try {
      await apiStore.deleteUploadedMedia(instance, uploadedMediaId);
      questionnaireWizardPageUiStore.questionnaire.logo = blankUploadedMedia();
    } catch (error) {
      // TODO - handle error
    }
  };

  const uploadMedia = async (mediaContent: File): Promise<void> => {
    setLoadingType('save');
    try {
      const uploadedMediaRef = await apiStore.uploadMedia(instance, questionnaireWizardPageUiStore.questionnaire.questionnaireId, mediaContent);
      const uploadedMedia = blankUploadedMedia();
      uploadedMedia.uploadedMediaId = uploadedMediaRef.uploadedMediaId;
      uploadedMedia.originalFileName = mediaContent.name;
      questionnaireWizardPageUiStore.questionnaire.media.push(uploadedMedia);
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoadingType(undefined);
    }
  };

  const deleteMedia = async (uploadedMediaId: number): Promise<void> => {
    try {
      await apiStore.deleteUploadedMedia(instance, uploadedMediaId);
      const deletedMediaIndex = questionnaireWizardPageUiStore.questionnaire.media.findIndex(medium => medium.uploadedMediaId === uploadedMediaId);
      questionnaireWizardPageUiStore.questionnaire.media.splice(deletedMediaIndex, 1);
    } catch (error) {
      // TODO - handle error
    }
  };

  useTitle('Winner Questions');

  useEffect(() => {
    preloadData();
    return () => {
      questionnaireWizardPageUiStore.resetStates();
    };
  }, []);

  const resolveStatus = (currentStepId: QuestionnaireStepId, stepId: string): StepRailStatus => {
    const parsedStepId = questionnaireStepper.parseStep(stepId);
    if (!parsedStepId) {
      return 'disabled';
    }

    const currentIndex = questionnaireWizardPageUiStore.visibleStepIds.findIndex(id => id === currentStepId);
    const stepIndex = questionnaireWizardPageUiStore.visibleStepIds.findIndex(id => id === parsedStepId);
    const isBeingValidated = questionnaireWizardPageUiStore.isBeingValidated(parsedStepId);
    const isValid = validateStep(parsedStepId);

    if (stepIndex === currentIndex) {
      return isBeingValidated && !isValid ? 'current invalid' : 'current';
    }

    if (stepIndex > currentIndex && !isBeingValidated) {
      return 'disabled';
    }

    return isValid ? 'valid' : 'invalid';
  };

  const resolveDescription = (stepId: string): string | undefined =>
    stepId === 'contact' ? CONTACT_DESCRIPTION : undefined;

  const renderWizard = (stepper: QuestionnaireStepperInstance): ReactElement => {
    const currentStepId = stepper.current.id;
    const currentIndex = questionnaireWizardPageUiStore.visibleStepIds.findIndex(stepId => stepId === currentStepId);
    const isFirstStep = currentIndex === 0;
    const isLastStep = currentIndex === questionnaireWizardPageUiStore.visibleStepIds.length - 1;
    const railSteps = questionnaireStepper.steps.filter(step => questionnaireWizardPageUiStore.visibleStepIds.includes(step.id));

    const submitAndReview = async (): Promise<void> => {
      const areCompletedStepsValid = await isWizardFormValid(currentStepId);
      if (!areCompletedStepsValid) {
        return;
      }

      await commitStep(currentStepId, currentStepId);
      navigate(`${RoutePaths.ReviewQuestionnaire}/${questionnaireWizardPageUiStore.questionnaireId}`, { replace: true });
    };

    const handleForward = async (): Promise<void> => {
      if (isLastStep) {
        await submitAndReview();
      } else {
        await stepper.goTo(questionnaireWizardPageUiStore.visibleStepIds[currentIndex + 1]);
      }
    };

    const handleBack = async (): Promise<void> => {
      if (!isFirstStep) {
        await stepper.goTo(questionnaireWizardPageUiStore.visibleStepIds[currentIndex - 1]);
      } else if (questionnaireWizardPageUiStore.winnerRole === 'Finalist') {
        await submitAndReview();
      } else {
        navigate(RoutePaths.Home, { replace: true });
      }
    };

    const handleExit = async (): Promise<void> => {
      const areCompletedStepsValid = await isWizardFormValid(currentStepId);
      if (!areCompletedStepsValid) {
        return;
      }

      await commitStep(currentStepId, currentStepId);
      navigate(RoutePaths.Home, { replace: true });
    };

    const handleSelectStep = async (stepId: string): Promise<void> => {
      const parsedStepId = questionnaireStepper.parseStep(stepId);
      if (parsedStepId) {
        await stepper.goTo(parsedStepId);
      }
    };

    return (
      <StepRail
        steps={railSteps}
        currentStepId={currentStepId}
        resolveStatus={stepId => resolveStatus(currentStepId, stepId)}
        onSelectStep={handleSelectStep}
        resolveDescription={resolveDescription}
      >
        {
          stepper.match({
            contact: () => (
              <ContactInformationSection />
            ),
            'event-attendees': () => (
              <EventAttendeesSection />
            ),
            logo: () => (
              <UploadLogoSection
                logo={questionnaireWizardPageUiStore.questionnaire.logo}
                onUploadLogo={uploadLogo}
                onDeleteLogo={deleteLogo}
              />
            ),
            media: () => (
              <UploadMediaSection
                onUploadMedia={uploadMedia}
                onDeleteMedia={deleteMedia}
              />
            ),
          })
        }
        <StepNav
          backText='Back'
          forwardText='Save & Continue'
          isExitDisplayed
          exitText={questionnaireWizardPageUiStore.isSubmitted ? 'Save & Exit' : 'Save as draft'}
          lastStepForwardText={questionnaireWizardPageUiStore.isSubmitted && questionnaireWizardPageUiStore.winnerRole === 'Finalist' ? 'Save' : 'Submit'}
          isLastStep={isLastStep}
          onBack={handleBack}
          onForward={handleForward}
          onExit={handleExit}
        />
      </StepRail>
    );
  };

  if (isForbiddenAccess) {
    return <NoAccess />;
  }

  return (
    <main className='questionnaire-wizard-page'>
      {loadingType === 'save' && <Loading />}
      <FlexBox
        isFullWidth
        aligns='center'
        className='questionnaire-wizard-page__title'
      >
        <FlexBox aligns='center'>
          <Text
            block
            weight='semibold'
            size={400}
            as='h1'
          >
            {questionnaireWizardPageUiStore.winnerRole !== 'Finalist' ? 'Complete winner questions ' : 'Complete finalist questions'}
          </Text>
        </FlexBox>
        <Text
          block
          weight='semibold'
          size={500}
          as='h2'
        >
          {questionnaireWizardPageUiStore.awardedNomination?.awardTitle}
        </Text>
      </FlexBox>
      <div className='questionnaire-wrapper'>
        {
          loadingType === 'load' ?
            <StepperSkeleton
              stepCount={questionnaireWizardPageUiStore.visibleStepIds.length}
            /> :
            <questionnaireStepper.Provider
              defaultStep={questionnaireWizardPageUiStore.initialStepId}
              beforeStepChange={(context) => handleBeforeStepChange(context.from.id, context.to.id)}
            >
              <QuestionnaireWizardSection
                render={renderWizard}
              />
            </questionnaireStepper.Provider>
        }
      </div>
    </main>
  );
};

export const QuestionnaireWizardPage = observer(QuestionnaireWizardPageImpl);
